import { PlayerState, StatId, Team, UpgradePath } from '../protocol/gen/warinspace/v2/common_pb.js';
import type { LobbyCloseReason } from '../protocol/gen/warinspace/v2/common_pb.js';
import type {
  EntitySpawn,
  FeatureSet,
  LobbyListing,
  LobbyState,
  ServerMessage,
} from '../protocol/gen/warinspace/v2/server_to_client_pb.js';
import { surrenderViewFrom, type SurrenderView } from './surrender';
import { teamStatsViewFrom, type TeamStatsView } from './team-stats';
import { localCue, positionalCue, type SoundCue } from '../audio/sounds';
import {
  BASE_TARGET_CONE_HALF_ANGLE,
  BASE_TARGET_DISTANCE_BIAS,
  DEATH_TIMER_MS,
  LERP,
  MAX_LERP_DISTANCE,
  TARGET_CONE_HALF_ANGLE,
} from './constants/feel';
import {
  BASE_START_HEALTH,
  MAP_HEIGHT,
  MAP_WIDTH,
  NAME_REQUEST_RETRY_MS,
  PLAYER_BASELINE,
  SCRAP_PICKUP_RADIUS,
} from './constants/world';
import { END_GAME } from './constants/hud';
import { productFrom, type Product } from './constants/products';
import { DEATH_TIPS } from './constants/tips';
import { EffectRandom } from './effects/random';

export interface Entity {
  readonly id: number;
  readonly kind: EntityKind;
  x: number;
  y: number;
  /** `displayX`/`displayY` chase `x`/`y` one-directionally; merging them feeds each render frame into the next one's target. */
  displayX: number;
  displayY: number;
  snapPosition?: boolean;
  angle: number;
  displayAngle: number;
  health: number;
  maxHealth: number;
  team: number;
  readonly states: Map<number, number>;
}

export type EntityKind = 'player' | 'scrap' | 'turret' | 'drone' | 'soccerBall' | 'controlPoint';

export interface PlayerEntity extends Entity {
  readonly kind: 'player';
  shipId: number;
  levels: [number, number, number];
  decalProductId: number;
  trailProductId: number;
  tagProductId: number;
  skinProductId: number;
  nameId: number;
}

export interface TurretEntity extends Entity {
  readonly kind: 'turret';
  turretType: number;
}

export interface DroneEntity extends Entity {
  readonly kind: 'drone';
  ownerPlayerId: number;
  ownerKnown: boolean;
  droneType: number;
  positionOffset: number;
  nextOffsetX: number;
  nextOffsetY: number;
  isUnder: boolean;
  xdirection: number;
  ydirection: number;
}

export interface ControlPointEntity extends Entity {
  readonly kind: 'controlPoint';
  pointType: number;
  influence: number;
}

export interface VisualEvent {
  readonly kind: 'hit' | 'miss' | 'emoji' | 'death' | 'suicideBomb' | 'damageNumber';
  readonly at: number;
  readonly shooterId?: number;
  readonly targetId?: number;
  readonly entityId?: number;
  readonly emojiIndex?: number;
  readonly arrivesAt?: number;
  readonly amount?: number;
}

/** Gains are what the game shipped with, not a mix decision to re-balance here. */
const SOUND_GAIN = {
  spawn: 0.4,
  destructionSelf: 0.55,
  destruction: 0.45,
  minorDestruction: 0.2,
  collectScrap: 0.2,
  entitySpawn: 0.2,
  shots: 0.2,
  state: 0.4,
  upgrade: 0.4,
} as const;

const SHOT_ATTENUATION_CAP = 0.8;

const MISS_TRACER_LENGTH = 600;

const TOO_CLOSE_RANGE_SQ = 60 * 60;

/** These are the wire enum's values, one higher than the simulation ids the table was written against (0 is reserved for unspecified). */
const AUDIBLE_STATES = new Set<number>([
  PlayerState.TIMED_SHIELD,
  PlayerState.STEALTH,
  PlayerState.ABSORBTION,
  PlayerState.DAMAGE_BOOSTED,
]);

const SUICIDE_DRONE_TYPE = 4;

const DRONE_ORBIT_AMPLITUDE = 130;
const DRONE_ORBIT_STEP = 0.07;
export const DRONE_ORBIT_PHASE_SPAN = 2 * Math.PI * 13;

function droneOrbitOffsetX(phase: number): number {
  return Math.sin(phase / 3) * Math.cos(phase / 7) * DRONE_ORBIT_AMPLITUDE;
}

function droneOrbitOffsetY(phase: number): number {
  return Math.cos(phase / 13) * Math.sin(phase / 5) * DRONE_ORBIT_AMPLITUDE;
}

const MAX_QUEUED_CUES = 64;

/** Both ends of the window get this margin so the countdown doesn't reach zero before the server's mode change actually lands. */
const GAME_MODE_TIME_SKEW_MS = 3000;

const MAX_CHAT_HISTORY = 64;

export interface WorldSetupState {
  baseAngle: number;
  baseDistance: number;
  baseRotationSpeed: number;
  maxX: number;
  maxY: number;
  ffa: boolean;
  baseMaxHealth: number;
}

export class World {
  private readonly random: EffectRandom;

  constructor(seed = 0x9e3779b9) {
    this.random = new EffectRandom(seed);
  }

  readonly entities = new Map<number, Entity>();

  /** Bumped by `resetWorld`; render-only state that outlives a single world (e.g. `EffectSystem`) watches this to know when to drop its own per-world state. */
  generation = 0;

  selfId = -1;
  selfTeam = 2;
  selfName: string | null = null;
  playing = false;
  dead = false;

  /** Owned by `SpectatorCamera`; the renderer reads it to ring the ship the camera is locked to, which is otherwise indistinguishable from the ones beside it. */
  spectatorFollowId: number | null = null;

  /** Team 2 is also the pre-`Init` default, so `playing` is the half that says this is a live watcher rather than someone still on the menu. */
  get spectating(): boolean {
    return this.playing && this.selfTeam === 2;
  }

  diedAt = 0;
  deathTip = '';

  returnToMenuRequested = false;

  /** Client-authoritative: this is truth, not a prediction to reconcile. S->C 0 (PositionReset) is an anti-cheat correction, not the other direction. */
  selfX = MAP_WIDTH / 2;
  selfY = MAP_HEIGHT / 2;
  selfAngle = 0;

  selfHealth: number = PLAYER_BASELINE.maxHealth;
  selfMaxHealth: number = PLAYER_BASELINE.maxHealth;
  selfMoveSpeed: number = PLAYER_BASELINE.moveSpeed;
  selfFiringRange: number = PLAYER_BASELINE.firingRange;
  selfDamage: number = PLAYER_BASELINE.damage;
  selfMaxDrones: number = PLAYER_BASELINE.maxDrones;

  selfShipId = 0;
  selfLevels: [number, number, number] = [0, 0, 0];

  selfAbilityIds: readonly number[] | null = null;
  evolutionAbilityIds: Readonly<Record<number, readonly number[]>> = {};
  readonly selfStates = new Map<number, number>();

  scrap = 0;
  moneySpentOnUpgrades = 0;
  abilityCooldownMs = 0;
  abilityCooldownSetAt = 0;

  blueBaseHealth = BASE_START_HEALTH;
  redBaseHealth = BASE_START_HEALTH;

  world: WorldSetupState = {
    baseAngle: 0,
    baseDistance: 0,
    baseRotationSpeed: 0,
    maxX: MAP_WIDTH,
    maxY: MAP_HEIGHT,
    ffa: false,
    baseMaxHealth: BASE_START_HEALTH,
  };

  /** Angle is resolved at receipt time (S->C 24), not page load, or the base is offset by however long the tab had been open. */
  worldSetupAt = 0;

  serverNum = 0;
  leaderboard: readonly LeaderboardRow[] = [];
  myRank = 0;
  ffaTargetId = -1;

  /** Minimap only, and everyone on the receiver's own side including the receiver — see `TeamPositions`. Arrives a few times a second, so it is smoothed in `interpolate` and drawn from its display fields; a teammate already in `entities` should be drawn from there instead. Empty under FFA. */
  readonly teamPositions = new Map<number, TeamMarkerState>();

  readonly nameCache = new Map<number, string>();
  readonly pendingNameRequests = new Map<number, number>();

  readonly visualEvents: VisualEvent[] = [];
  readonly notifications: { text: string; at: number }[] = [];

  readonly soundCues: SoundCue[] = [];

  /** `AccountSnapshot` replaces credits and inventory; `productsGranted` only adds. Swapping the two empties a player's wardrobe on every purchase. */
  credits = 0;
  readonly ownedProductIds = new Set<number>();
  readonly products = new Map<number, Product>();
  catalogueLoaded = false;

  equipment: Equipment = { decal: 0, trail: 0, tag: 0, shipSkins: [] };

  /** Distinguishes "owns nothing" from "wallet/wardrobe not yet stated" — both read as zero/empty otherwise. */
  accountStated = false;
  equipmentStated = false;

  matchInfo: MatchInfoState | null = null;
  matchInfoAt = 0;

  /** Stored as absolute times, not deltas — the message's deltas are from receipt, so a delta rendered later would count down from the wrong place. */
  gameModeStartAt: number | null = null;
  gameModeEndAt: number | null = null;

  worldPopulations: readonly number[] = [];

  readonly chat: ChatLine[] = [];

  chatVersion = 0;

  chatEnabled = false;

  respawnDelayMs = DEATH_TIMER_MS;

  matchCountdownMs = 0;

  matchCountdownAt = 0;

  lobby: LobbyState | null = null;

  lobbies: readonly LobbyListing[] = [];

  lobbyVersion = 0;

  lobbyClosed: { reason: LobbyCloseReason; at: number } | null = null;

  surrender: SurrenderView | null = null;

  teamStats: TeamStatsView | null = null;
  features: FeatureSet | null = null;
  featuresVersion = 0;

  notify(text: string, now: number): void {
    this.notifications.push({ text, at: now });
  }

  /** Exhaustive switch over the envelope's oneof: the compiler flags a schema addition not handled here as `never`, not as state that silently drops. */
  apply(message: ServerMessage, now: number): void {
    const payload = message.payload;
    switch (payload.case) {
      case 'positionReset':
        this.selfX = payload.value.x;
        this.selfY = payload.value.y;
        break;

      case 'init':
        this.selfTeam = teamFromWire(payload.value.team);
        this.selfId = payload.value.selfId;
        this.selfShipId = payload.value.shipId;
        this.selfLevels = [
          payload.value.allUpgradeLevels[0] ?? 0,
          payload.value.allUpgradeLevels[1] ?? 0,
          payload.value.allUpgradeLevels[2] ?? 0,
        ];
        this.playing = true;
        this.dead = false;
        this.returnToMenuRequested = false;
        this.cue(localCue('spawn', SOUND_GAIN.spawn));
        break;

      case 'selfReset':
        this.resetWorld();
        break;

      case 'dead':
        this.playing = false;
        break;

      case 'selfHealth': {
        const wasAlive = this.selfHealth > 0;
        const delta = this.selfHealth - payload.value.health;
        this.selfHealth = payload.value.health;
        if (wasAlive && delta !== 0) {
          this.visualEvents.push({
            kind: 'damageNumber',
            at: now,
            entityId: this.selfId,
            amount: delta,
          });
        }
        if (wasAlive && this.selfHealth <= 0) {
          this.dead = true;
          this.diedAt = now;
          this.deathTip = DEATH_TIPS[this.random.int(0, DEATH_TIPS.length - 1)] ?? '';
          this.cue(localCue('destructionSelf', SOUND_GAIN.destructionSelf));
          this.cue(localCue('destructionSelf2', SOUND_GAIN.destructionSelf));
        }
        break;
      }

      case 'scrapChange':
        this.scrap = payload.value.scrap;
        break;

      case 'moneySpentOnUpgrades':
        this.moneySpentOnUpgrades = Number(payload.value.amount);
        break;

      case 'activeCooldown':
        this.abilityCooldownMs = payload.value.remainingMs;
        this.abilityCooldownSetAt = now;
        break;

      case 'selfStatChange':
        this.applyStatChange(payload.value.stat, payload.value.value);
        break;

      case 'selfStateAdd': {
        const first = !this.selfStates.has(payload.value.state);
        increment(this.selfStates, payload.value.state);
        if (first && AUDIBLE_STATES.has(payload.value.state)) {
          this.cue(localCue('powerUp', SOUND_GAIN.state));
        }
        break;
      }

      case 'selfStateRemove':
        decrement(this.selfStates, payload.value.state);
        if (!this.selfStates.has(payload.value.state) && AUDIBLE_STATES.has(payload.value.state)) {
          this.cue(localCue('powerDown', SOUND_GAIN.state));
        }
        break;

      case 'systemLevel':
        this.applyPathLevel(payload.value.path, payload.value.level);
        this.cue(localCue('upgrade', SOUND_GAIN.upgrade));
        break;

      case 'shipChanged':
        this.selfShipId = payload.value.shipId;
        this.cue(localCue('upgradeShip', SOUND_GAIN.upgrade));
        break;

      case 'selfLoadoutAbilities':
        this.selfAbilityIds = [...payload.value.allAbilityIds];
        this.evolutionAbilityIds = Object.fromEntries(
          payload.value.allEvolutionChoices.map((choice) => [
            choice.shipId,
            [...choice.allAbilityIds],
          ]),
        );
        break;

      case 'entitySpawn':
        this.applySpawn(payload.value);
        break;

      case 'entityDespawn':
        this.entities.delete(payload.value.entityId);
        break;

      case 'entityMove': {
        const entity = this.entities.get(payload.value.entityId);
        if (entity === undefined) break;
        entity.angle = Math.atan2(
          payload.value.y - entity.displayY,
          payload.value.x - entity.displayX,
        );
        entity.x = payload.value.x;
        entity.y = payload.value.y;
        break;
      }

      case 'entityAngle': {
        const entity = this.entities.get(payload.value.entityId);
        if (entity !== undefined) entity.angle = payload.value.angleRadians;
        break;
      }

      case 'entityHealth': {
        const entity = this.entities.get(payload.value.entityId);
        if (entity === undefined) break;
        if (entity.kind === 'controlPoint') {
          /** Overloaded: for a control point this field is influence, not health. */
          (entity as ControlPointEntity).influence = payload.value.health;
          break;
        }
        const wasAlive = entity.health > 0;
        entity.health = payload.value.health;
        if (!wasAlive && payload.value.health > 0) entity.snapPosition = true;
        if (wasAlive && payload.value.health <= 0) {
          this.visualEvents.push({ kind: 'death', at: now, entityId: entity.id });
          this.cueDestruction(entity);
        }
        break;
      }

      case 'otherStateAdd': {
        const entity = this.entities.get(payload.value.entityId);
        if (entity !== undefined) increment(entity.states, payload.value.state);
        break;
      }

      case 'otherStateRemove': {
        const entity = this.entities.get(payload.value.entityId);
        if (entity !== undefined) decrement(entity.states, payload.value.state);
        break;
      }

      case 'otherShipChange': {
        const entity = this.entities.get(payload.value.entityId);
        if (entity !== undefined && entity.kind === 'player') {
          const player = entity as PlayerEntity;
          player.shipId = payload.value.shipId;
          player.skinProductId = payload.value.skinProductId;
        }
        break;
      }

      case 'selfHitBy': {
        this.visualEvents.push({
          kind: 'hit',
          at: now,
          shooterId: payload.value.shooterId,
          targetId: this.selfId,
        });
        const shooter = this.entities.get(payload.value.shooterId);
        if (shooter !== undefined) {
          shooter.angle = Math.atan2(this.selfY - shooter.displayY, this.selfX - shooter.displayX);
          this.cueShot(shooter.displayX, shooter.displayY, this.selfX, this.selfY);
        }
        break;
      }

      case 'entityHitEntity': {
        this.visualEvents.push({
          kind: 'hit',
          at: now,
          shooterId: payload.value.shooterId,
          targetId: payload.value.targetId,
        });
        const shooter = this.entities.get(payload.value.shooterId);
        const target = this.entities.get(payload.value.targetId);
        if (shooter !== undefined && target !== undefined) {
          shooter.angle = Math.atan2(
            target.displayY - shooter.displayY,
            target.displayX - shooter.displayX,
          );
          this.cueShot(shooter.displayX, shooter.displayY, target.displayX, target.displayY);
        }
        break;
      }

      case 'entityMiss': {
        this.visualEvents.push({ kind: 'miss', at: now, shooterId: payload.value.shooterId });
        const shooter = this.entities.get(payload.value.shooterId);
        if (shooter !== undefined) {
          this.cueShot(
            shooter.displayX,
            shooter.displayY,
            shooter.displayX + Math.cos(shooter.angle) * MISS_TRACER_LENGTH,
            shooter.displayY + Math.sin(shooter.angle) * MISS_TRACER_LENGTH,
          );
        }
        break;
      }

      case 'emoji':
        this.visualEvents.push({
          kind: 'emoji',
          at: now,
          entityId: payload.value.entityId,
          emojiIndex: payload.value.emojiIndex,
        });
        break;

      case 'suicideBombAway':
        this.visualEvents.push({
          kind: 'suicideBomb',
          at: now,
          shooterId: payload.value.droneId,
          targetId: payload.value.targetId,
          /** Raw server figure; RTT correction is applied by the caller, which owns the RTT estimate. */
          arrivesAt: now + payload.value.timeToHitMs,
        });
        break;

      case 'worldSetup':
        this.worldSetupAt = now;
        this.world = {
          baseAngle: payload.value.baseAngleRadians,
          baseDistance: payload.value.baseDistance,
          /** May legitimately be Infinity (stationary-base mode); the renderer divides by it to zero. Do not normalise it here. */
          baseRotationSpeed: payload.value.baseRotationSpeed,
          maxX: payload.value.maxX,
          maxY: payload.value.maxY,
          ffa: payload.value.ffa,
          baseMaxHealth: payload.value.baseMaxHealth,
        };
        this.chatEnabled = payload.value.chatEnabled;
        if (payload.value.respawnDelayMs > 0) this.respawnDelayMs = payload.value.respawnDelayMs;
        break;

      case 'baseHp':
        this.blueBaseHealth = payload.value.blueHp;
        this.redBaseHealth = payload.value.redHp;
        break;

      case 'serverId':
        this.serverNum = payload.value.serverNum;
        this.chat.length = 0;
        this.chatVersion++;
        this.resetWorld();
        break;

      case 'leaderboard':
        this.leaderboard = payload.value.allEntries.map((entry) => ({
          nameId: entry.nameId,
          team: teamFromWire(entry.team),
          points: entry.points,
        }));
        break;

      case 'myRank':
        this.myRank = payload.value.rank;
        break;

      case 'teamPositions': {
        const stale = new Set(this.teamPositions.keys());
        for (const marker of payload.value.allPositions) {
          stale.delete(marker.playerId);
          const known = this.teamPositions.get(marker.playerId);
          const team = teamFromWire(marker.team);
          if (known === undefined) {
            this.teamPositions.set(marker.playerId, {
              x: marker.x,
              y: marker.y,
              angle: marker.angleRadians,
              displayX: marker.x,
              displayY: marker.y,
              displayAngle: marker.angleRadians,
              team,
            });
            continue;
          }
          known.x = marker.x;
          known.y = marker.y;
          known.angle = marker.angleRadians;
          known.team = team;
        }
        for (const id of stale) this.teamPositions.delete(id);
        break;
      }

      case 'ffaTarget':
        this.ffaTargetId = payload.value.playerId;
        break;

      case 'nameReply':
        this.nameCache.set(payload.value.nameId, payload.value.name);
        this.pendingNameRequests.delete(payload.value.nameId);
        break;

      case 'nameRemoved':
        this.nameCache.delete(payload.value.nameId);
        this.pendingNameRequests.delete(payload.value.nameId);
        break;

      case 'notification':
        this.notify(payload.value.text, now);
        break;

      case 'serverRestarting':
      case 'serverBuild':
      case 'pong':
        break;

      case 'loginChange':
        /** This client authenticates over HTTP; acting on this would resurrect the in-socket flow. */
        break;

      case 'accountSnapshot':
        this.credits = Number(payload.value.creditBalance);
        this.ownedProductIds.clear();
        for (const id of payload.value.allOwnedProductIds) this.ownedProductIds.add(id);
        this.accountStated = true;
        break;

      case 'productsGranted':
        this.credits = Number(payload.value.creditBalance);
        for (const id of payload.value.addedProductIds) this.ownedProductIds.add(id);
        this.accountStated = true;
        break;

      case 'productList':
        this.products.clear();
        for (const row of payload.value.allProducts) {
          this.products.set(row.productId, productFrom(row));
        }
        this.catalogueLoaded = true;
        break;

      case 'equipment':
        this.equipment = {
          decal: payload.value.decalProductId,
          trail: payload.value.trailProductId,
          tag: payload.value.tagProductId,
          shipSkins: [...payload.value.allShipSkinProductIds],
        };
        this.equipmentStated = true;
        break;

      case 'matchInfo':
        this.matchInfo = {
          gainedCredits: payload.value.gainedCredits,
          rank: payload.value.rank,
          maxPlayers: payload.value.maxPlayers,
          kills: payload.value.kills,
          assists: payload.value.assists,
          deaths: payload.value.deaths,
          teamSwapped: payload.value.teamSwapped,
          autoBalanced: payload.value.autoBalanced,
          won: payload.value.won,
          totalHeal: Number(payload.value.totalHeal),
          damageToBase: Number(payload.value.damageToBase),
          mvpScore: payload.value.mvpScore,
        };
        this.matchInfoAt = now;
        /** The server's balance here is pre-payout; without adding gainedCredits the shop shows the old balance until the next snapshot. */
        this.credits += payload.value.gainedCredits;
        break;

      case 'gameModeTimes': {
        const start = payload.value.startDeltaMs;
        const end = payload.value.endDeltaMs;
        this.gameModeStartAt =
          start === undefined ? null : now + Number(start) + GAME_MODE_TIME_SKEW_MS;
        this.gameModeEndAt = end === undefined ? null : now + Number(end) + GAME_MODE_TIME_SKEW_MS;
        break;
      }

      case 'serverPops':
        this.worldPopulations = [...payload.value.allWorldPopulations];
        break;

      case 'chat':
        this.chat.push({
          team: teamFromWire(payload.value.team),
          sender: payload.value.senderName,
          text: payload.value.text,
        });
        if (this.chat.length > MAX_CHAT_HISTORY) this.chat.shift();
        this.chatVersion++;
        break;

      case 'surrenderState':
        this.surrender = surrenderViewFrom(payload.value);
        break;

      case 'teamStats':
        this.teamStats = teamStatsViewFrom(payload.value);
        break;

      case 'lobbyState':
        this.lobby = payload.value;
        this.lobbyVersion++;
        break;

      case 'matchCountdown':
        this.matchCountdownMs = payload.value.remainingMs;
        this.matchCountdownAt = now;
        break;

      case 'featureSet':
        this.features = payload.value;
        this.featuresVersion++;
        break;

      case 'lobbyList':
        this.lobbies = payload.value.allLobbies;
        this.lobbyVersion++;
        break;

      case 'lobbyClosed':
        this.lobby = null;
        this.surrender = null;
        this.teamStats = null;
        this.lobbyClosed = { reason: payload.value.reason, at: now };
        this.lobbyVersion++;
        this.playing = false;
        this.resetWorld();
        break;

      case undefined:
        break;

      default:
        payload satisfies never;
        break;
    }
  }

  private applySpawn(spawn: EntitySpawn): void {
    const id = spawn.entityId;
    const body = spawn.body;
    switch (body.case) {
      case 'player': {
        const player: PlayerEntity = {
          id,
          kind: 'player',
          x: body.value.x,
          y: body.value.y,
          displayX: body.value.x,
          displayY: body.value.y,
          angle: body.value.angleRadians,
          displayAngle: body.value.angleRadians,
          health: body.value.health,
          maxHealth: body.value.maxHealth,
          team: teamFromWire(body.value.team),
          states: new Map(),
          shipId: body.value.shipId,
          levels: [
            body.value.allPathLevels[0] ?? 0,
            body.value.allPathLevels[1] ?? 0,
            body.value.allPathLevels[2] ?? 0,
          ],
          decalProductId: body.value.cosmetics?.decalProductId ?? 0,
          trailProductId: body.value.cosmetics?.trailProductId ?? 0,
          tagProductId: body.value.cosmetics?.tagProductId ?? 0,
          skinProductId: body.value.cosmetics?.skinProductId ?? 0,
          nameId: body.value.nameId,
        };
        for (const state of body.value.allStates) increment(player.states, state);
        this.entities.set(id, player);
        break;
      }

      case 'scrap':
        this.entities.set(id, stationary(id, 'scrap', body.value.x, body.value.y));
        break;

      case 'soccerBall':
        this.entities.set(id, stationary(id, 'soccerBall', body.value.x, body.value.y));
        break;

      case 'turret': {
        const turret: TurretEntity = {
          ...stationary(id, 'turret', body.value.x, body.value.y),
          kind: 'turret',
          health: body.value.health,
          maxHealth: body.value.maxHealth,
          team: teamFromWire(body.value.team),
          turretType: body.value.turretType,
        };
        turret.angle = (id * 2.399963) % (Math.PI * 2);
        turret.displayAngle = turret.angle;
        this.entities.set(id, turret);
        break;
      }

      case 'drone': {
        const origin = this.droneOrigin(body.value.ownerPlayerId);
        const phase = this.random.next() * DRONE_ORBIT_PHASE_SPAN;
        const drone: DroneEntity = {
          ...stationary(id, 'drone', origin?.x ?? this.selfX, origin?.y ?? this.selfY),
          kind: 'drone',
          team: origin?.team ?? this.selfTeam,
          ownerKnown: origin !== null,
          ownerPlayerId: body.value.ownerPlayerId,
          droneType: body.value.droneType,
          positionOffset: phase,
          nextOffsetX: droneOrbitOffsetX(phase),
          nextOffsetY: droneOrbitOffsetY(phase),
          isUnder: this.random.next() < 0.5,
          xdirection: 1,
          ydirection: 1,
        };
        this.entities.set(id, drone);
        if (body.value.ownerPlayerId === this.selfId) {
          this.cue(localCue('entitySpawn', SOUND_GAIN.entitySpawn));
        }
        break;
      }

      case 'controlPoint': {
        const point: ControlPointEntity = {
          ...stationary(id, 'controlPoint', body.value.x, body.value.y),
          kind: 'controlPoint',
          pointType: body.value.controlPointType,
          influence: body.value.influence,
        };
        this.entities.set(id, point);
        break;
      }

      case undefined:
        break;

      default:
        body satisfies never;
        break;
    }
  }

  queueOwnShotSound(): void {
    this.cueShot(this.selfX, this.selfY, this.selfX, this.selfY);
  }

  /** Drops the sound if the queue is full: `playCues` drains it every browser frame, but the unit suite and node probes never call it. */
  private cue(cue: SoundCue): void {
    if (this.soundCues.length >= MAX_QUEUED_CUES) return;
    this.soundCues.push(cue);
  }

  private cueDestruction(entity: Entity): void {
    if (entity.kind === 'drone') {
      if ((entity as DroneEntity).droneType === SUICIDE_DRONE_TYPE) return;
      this.cue(localCue('destruction', SOUND_GAIN.minorDestruction));
      return;
    }
    if (entity.kind === 'turret') {
      this.cue(localCue('destruction', SOUND_GAIN.minorDestruction));
      return;
    }
    this.cue(
      positionalCue('destruction', SOUND_GAIN.destruction, entity.displayX, entity.displayY),
    );
  }

  private cueShot(x1: number, y1: number, x2: number, y2: number): void {
    const near: readonly [number, number] =
      this.hypotToSelf(x1, y1) <= this.hypotToSelf(x2, y2) ? [x1, y1] : [x2, y2];
    this.cue(positionalCue('shots', SOUND_GAIN.shots, near[0], near[1], SHOT_ATTENUATION_CAP));
  }

  private hypotToSelf(x: number, y: number): number {
    return Math.hypot(x - this.selfX, y - this.selfY);
  }

  private applyPathLevel(path: UpgradePath, level: number): void {
    switch (path) {
      case UpgradePath.ZERO:
        this.selfLevels[0] = level;
        break;
      case UpgradePath.ONE:
        this.selfLevels[1] = level;
        break;
      case UpgradePath.TWO:
        this.selfLevels[2] = level;
        break;
      default:
        break;
    }
  }

  /** Per-frame exponential smoothing, not time-based — deliberately frame-rate dependent (144 Hz converges faster than 60), matching the legacy client. `frameRateIndependentDtMs` is the opt-in fix. */
  interpolate(frameRateIndependentDtMs?: number): void {
    for (const entity of this.entities.values()) {
      if (entity.kind === 'drone') continue;
      if (entity.kind === 'scrap' || entity.kind === 'turret' || entity.kind === 'controlPoint') {
        entity.displayX = entity.x;
        entity.displayY = entity.y;
        entity.displayAngle = entity.angle;
        continue;
      }

      const positionAlpha =
        frameRateIndependentDtMs === undefined
          ? LERP.playerPosition
          : alphaForDt(LERP.playerPosition, frameRateIndependentDtMs);
      const angleAlpha =
        frameRateIndependentDtMs === undefined
          ? LERP.playerAngle
          : alphaForDt(LERP.playerAngle, frameRateIndependentDtMs);

      const dx = entity.x - entity.displayX;
      const dy = entity.y - entity.displayY;
      if (
        entity.snapPosition === true ||
        dx * dx + dy * dy > MAX_LERP_DISTANCE * MAX_LERP_DISTANCE
      ) {
        entity.snapPosition = false;
        entity.displayX = entity.x;
        entity.displayY = entity.y;
        entity.displayAngle = entity.angle;
        continue;
      }

      entity.displayX += dx * positionAlpha;
      entity.displayY += dy * positionAlpha;

      /** Unwrap across the +-pi seam before lerping, or a ship crossing due-left spins the long way round. */
      if (entity.displayAngle > entity.angle + Math.PI) entity.displayAngle -= Math.PI * 2;
      if (entity.displayAngle < entity.angle - Math.PI) entity.displayAngle += Math.PI * 2;
      entity.displayAngle += (entity.angle - entity.displayAngle) * angleAlpha;
    }

    for (const entity of this.entities.values()) {
      if (entity.kind === 'drone') this.advanceDroneOrbit(entity as DroneEntity);
    }

    const markerAlpha =
      frameRateIndependentDtMs === undefined
        ? LERP.teamMarker
        : alphaForDt(LERP.teamMarker, frameRateIndependentDtMs);
    for (const marker of this.teamPositions.values()) {
      const dx = marker.x - marker.displayX;
      const dy = marker.y - marker.displayY;
      if (dx * dx + dy * dy > MAX_LERP_DISTANCE * MAX_LERP_DISTANCE) {
        marker.displayX = marker.x;
        marker.displayY = marker.y;
        marker.displayAngle = marker.angle;
        continue;
      }
      marker.displayX += dx * markerAlpha;
      marker.displayY += dy * markerAlpha;
      if (marker.displayAngle > marker.angle + Math.PI) marker.displayAngle -= Math.PI * 2;
      if (marker.displayAngle < marker.angle - Math.PI) marker.displayAngle += Math.PI * 2;
      marker.displayAngle += (marker.angle - marker.displayAngle) * markerAlpha;
    }
  }

  private droneOrigin(ownerPlayerId: number): { x: number; y: number; team: number } | null {
    if (ownerPlayerId === this.selfId) {
      return { x: this.selfX, y: this.selfY, team: this.selfTeam };
    }
    const owner = this.entities.get(ownerPlayerId);
    if (owner === undefined) return null;
    return { x: owner.displayX, y: owner.displayY, team: owner.team };
  }

  private advanceDroneOrbit(drone: DroneEntity): void {
    const origin = this.droneOrigin(drone.ownerPlayerId);
    if (origin === null) {
      drone.ownerKnown = false;
      return;
    }
    drone.ownerKnown = true;
    drone.team = origin.team;
    const originX = origin.x;
    const originY = origin.y;

    const prevX = drone.nextOffsetX;
    const prevY = drone.nextOffsetY;
    drone.positionOffset += DRONE_ORBIT_STEP;
    drone.nextOffsetX = droneOrbitOffsetX(drone.positionOffset);
    drone.nextOffsetY = droneOrbitOffsetY(drone.positionOffset);

    const angle = Math.atan2(drone.nextOffsetY - prevY, drone.nextOffsetX - prevX);
    drone.angle = angle;
    drone.displayX = originX + prevX;
    drone.displayY = originY + prevY;

    const newXDirection = Math.sign(
      Math.sin(drone.positionOffset / 3) - Math.sin((drone.positionOffset - DRONE_ORBIT_STEP) / 3),
    );
    const newYDirection = Math.sign(
      Math.cos(drone.positionOffset / 13) -
        Math.cos((drone.positionOffset - DRONE_ORBIT_STEP) / 13),
    );
    if (newXDirection !== drone.xdirection || newYDirection !== drone.ydirection) {
      drone.isUnder = !drone.isUnder;
    }
    drone.xdirection = newXDirection;
    drone.ydirection = newYDirection;

    if (drone.displayAngle > angle + Math.PI) drone.displayAngle -= Math.PI * 2;
    if (drone.displayAngle < angle - Math.PI) drone.displayAngle += Math.PI * 2;
    drone.displayAngle += (angle - drone.displayAngle) * LERP.droneAngle;
  }

  /** Counted from replication, not tracked as a number — a tracked count drifts on a despawn this client missed, and the HUD would say 3/3 forever. */
  get selfDrones(): number {
    let count = 0;
    for (const entity of this.entities.values()) {
      if (entity.kind === 'drone' && (entity as DroneEntity).ownerPlayerId === this.selfId) count++;
    }
    return count;
  }

  abilityCooldownRemaining(now: number): number {
    return Math.max(0, this.abilityCooldownMs - (now - this.abilityCooldownSetAt));
  }

  /** `baseRotationSpeed` may be Infinity (stationary-base mode), which divides to zero — the right answer. Do not guard it. */
  basePosition(team: number, now: number): { x: number; y: number; angle: number } {
    const { baseAngle, baseDistance, baseRotationSpeed, maxX, maxY } = this.world;
    const angle = baseAngle + (now - this.worldSetupAt) / baseRotationSpeed + team * Math.PI;
    return {
      x: maxX / 2 + Math.cos(angle) * baseDistance,
      y: maxY / 2 + Math.sin(angle) * baseDistance,
      angle,
    };
  }

  basePositionForEntityId(id: number, now: number): { x: number; y: number } | null {
    if (!isBaseEntityId(id)) return null;
    const { x, y } = this.basePosition(id, now);
    return { x, y };
  }

  nameFor(nameId: number): string | null {
    return this.nameCache.get(nameId) ?? null;
  }

  /** Retries because name ids are recycled, so one unanswered request would strand every later player given that id. */
  missingNameIds(now: number): number[] {
    const missing: number[] = [];

    const want = (nameId: number): void => {
      if (this.nameCache.has(nameId)) return;
      const requestedAt = this.pendingNameRequests.get(nameId);
      if (requestedAt !== undefined && now - requestedAt < NAME_REQUEST_RETRY_MS) return;
      this.pendingNameRequests.set(nameId, now);
      missing.push(nameId);
    };

    for (const entity of this.entities.values()) {
      if (entity.kind === 'player') want((entity as PlayerEntity).nameId);
    }

    for (const entry of this.leaderboard) want(entry.nameId);

    return missing;
  }

  queueOwnShot(targetId: number | null, now: number): void {
    this.visualEvents.push(
      targetId === null
        ? { kind: 'miss', at: now, shooterId: this.selfId }
        : { kind: 'hit', at: now, shooterId: this.selfId, targetId },
    );
  }

  /** `targetType`: 0 enemy (own team too under FFA), 1 ally, 2 both. May return self. */
  closestPlayerToPoint(x: number, y: number, targetType: number): number | null {
    let lowestDistSquared = Infinity;
    let closestId: number | null = null;
    for (const entity of this.entities.values()) {
      if (entity.kind !== 'player' || entity.health <= 0) continue;
      const matches =
        (targetType === 0 && (entity.team !== this.selfTeam || this.world.ffa)) ||
        (targetType === 1 && entity.team === this.selfTeam) ||
        targetType === 2;
      if (!matches) continue;
      const dx = entity.displayX - x;
      const dy = entity.displayY - y;
      const distSquared = dx * dx + dy * dy;
      if (distSquared < lowestDistSquared) {
        lowestDistSquared = distSquared;
        closestId = entity.id;
      }
    }
    return closestId;
  }

  /** Aims at the drawn (interpolated) position, not the raw one. Null is a miss the caller must still send, not a no-op. */
  pickFireTarget(now = 0): number | null {
    let best: number | null = null;
    let bestDistanceSq = Number.POSITIVE_INFINITY;
    const rangeSq = this.selfFiringRange * this.selfFiringRange;

    for (const entity of this.entities.values()) {
      if (entity.id === this.selfId) continue;
      if (entity.team === this.selfTeam && !this.world.ffa) continue;
      if (entity.kind !== 'player' && entity.kind !== 'turret' && entity.kind !== 'drone') continue;
      if (entity.health <= 0) continue;

      const dx = entity.displayX - this.selfX;
      const dy = entity.displayY - this.selfY;
      const distanceSq = dx * dx + dy * dy;
      if (distanceSq > rangeSq || distanceSq >= bestDistanceSq) continue;

      if (distanceSq > TOO_CLOSE_RANGE_SQ) {
        let delta = Math.atan2(dy, dx) - this.selfAngle;
        while (delta > Math.PI) delta -= Math.PI * 2;
        while (delta < -Math.PI) delta += Math.PI * 2;
        if (Math.abs(delta) > TARGET_CONE_HALF_ANGLE) continue;
      }

      best = entity.id;
      bestDistanceSq = distanceSq;
    }

    if (this.selfTeam === 0 || this.selfTeam === 1) {
      const enemyBaseId = this.selfTeam === 0 ? 1 : 0;
      const base = this.basePositionForEntityId(enemyBaseId, now);
      if (base !== null) {
        const dx = base.x - this.selfX;
        const dy = base.y - this.selfY;
        const distanceSq = dx * dx + dy * dy;
        if (distanceSq <= rangeSq && distanceSq - BASE_TARGET_DISTANCE_BIAS < bestDistanceSq) {
          let delta = Math.atan2(dy, dx) - this.selfAngle;
          while (delta > Math.PI) delta -= Math.PI * 2;
          while (delta < -Math.PI) delta += Math.PI * 2;
          if (Math.abs(delta) <= BASE_TARGET_CONE_HALF_ANGLE) {
            best = enemyBaseId;
            bestDistanceSq = distanceSq;
          }
        }
      }
    }

    return best;
  }

  /** Local and never reconciled. The threshold is `r^2 + scrapRadius^2`, not `(r + scrap)^2`. */
  collectScrapInRange(magnetRadius: number): void {
    const thresholdSquared =
      magnetRadius * magnetRadius + SCRAP_PICKUP_RADIUS * SCRAP_PICKUP_RADIUS;
    for (const [id, entity] of this.entities) {
      if (entity.kind !== 'scrap') continue;
      const dx = entity.x - this.selfX;
      const dy = entity.y - this.selfY;
      if (dx * dx + dy * dy >= thresholdSquared) continue;
      this.entities.delete(id);
      this.cue(localCue('collectScrap', SOUND_GAIN.collectScrap));
    }
  }

  /** Clears replicated state but keeps the session. Ship id and upgrade levels are restated by the next `Init`, so zeroing them here cannot outlive a spawn. */
  resetWorld(): void {
    this.generation++;
    this.entities.clear();
    this.selfStates.clear();
    this.visualEvents.length = 0;
    this.soundCues.length = 0;
    this.leaderboard = [];
    this.myRank = 0;
    this.ffaTargetId = -1;
    this.teamPositions.clear();

    this.dead = false;
    this.deathTip = '';
    this.selfShipId = 0;
    this.selfLevels = [0, 0, 0];
    this.selfAbilityIds = null;
    this.evolutionAbilityIds = {};
    this.selfHealth = PLAYER_BASELINE.maxHealth;
    this.selfMaxHealth = PLAYER_BASELINE.maxHealth;
    this.selfMoveSpeed = PLAYER_BASELINE.moveSpeed;
    this.selfFiringRange = PLAYER_BASELINE.firingRange;
    this.selfDamage = PLAYER_BASELINE.damage;
    this.selfMaxDrones = PLAYER_BASELINE.maxDrones;
    this.scrap = 0;
    this.moneySpentOnUpgrades = 0;
    this.abilityCooldownMs = 0;
    this.blueBaseHealth = BASE_START_HEALTH;
    this.redBaseHealth = BASE_START_HEALTH;
    /** Name ids are server-global, not per-world; clearing this would re-request every name after a world swap. */
  }

  /** Separate from `resetWorld`, which a round reset also calls: that must not clear the wallet. */
  resetAccount(): void {
    this.credits = 0;
    this.ownedProductIds.clear();
    this.equipment = { decal: 0, trail: 0, tag: 0, shipSkins: [] };
    this.accountStated = false;
    this.equipmentStated = false;
  }

  matchOverDue(now: number): boolean {
    return this.matchInfo !== null && now - this.matchInfoAt >= END_GAME.lifetimeMs;
  }

  dropSoundCues(): void {
    this.soundCues.length = 0;
  }

  exitMatch(): void {
    this.playing = false;
    this.returnToMenuRequested = false;
    this.surrender = null;
    this.teamStats = null;
    this.matchInfo = null;
    this.matchInfoAt = 0;
    this.resetWorld();
  }

  private applyStatChange(stat: StatId, value: number): void {
    switch (stat) {
      case StatId.MOVE_SPEED:
        this.selfMoveSpeed = value;
        break;
      case StatId.MAX_HEALTH:
        this.selfMaxHealth = value;
        break;
      case StatId.FIRING_RANGE:
        this.selfFiringRange = value;
        break;
      case StatId.DAMAGE:
        this.selfDamage = value;
        break;
      case StatId.MAX_DRONES:
        this.selfMaxDrones = value;
        break;
      default:
        break;
    }
  }
}

function stationary(id: number, kind: EntityKind, x: number, y: number): Entity {
  return {
    id,
    kind,
    x,
    y,
    displayX: x,
    displayY: y,
    angle: 0,
    displayAngle: 0,
    health: 1,
    maxHealth: 1,
    team: 2,
    states: new Map(),
  };
}

function increment(counts: Map<number, number>, key: number): void {
  counts.set(key, (counts.get(key) ?? 0) + 1);
}

function decrement(counts: Map<number, number>, key: number): void {
  const next = (counts.get(key) ?? 0) - 1;
  if (next <= 0) counts.delete(key);
  else counts.set(key, next);
}

export function alphaForDt(perFrameAlpha: number, dtMs: number): number {
  const nominalFrameMs = 1000 / 60;
  return 1 - (1 - perFrameAlpha) ** (dtMs / nominalFrameMs);
}

export interface TeamMarkerState {
  x: number;
  y: number;
  angle: number;
  displayX: number;
  displayY: number;
  displayAngle: number;
  team: number;
}

export interface Equipment {
  decal: number;
  trail: number;
  tag: number;
  shipSkins: readonly number[];
}

export interface MatchInfoState {
  readonly gainedCredits: number;
  readonly rank: number;
  readonly maxPlayers: number;
  readonly kills: number;
  readonly assists: number;
  readonly deaths: number;
  readonly teamSwapped: boolean;
  readonly autoBalanced: boolean;
  readonly won: boolean;
  readonly totalHeal: number;
  readonly damageToBase: number;
  readonly mvpScore: number;
}

export function isBaseEntityId(id: number): id is 0 | 1 {
  return id === 0 || id === 1;
}

export interface ChatLine {
  readonly team: number;
  readonly sender: string;
  readonly text: string;
}

/** The server's own lines: no sender, and no team to colour them by. A player always has one. */
export function isSystemChat(line: ChatLine): boolean {
  return line.sender === '' && line.team !== 0 && line.team !== 1;
}

export interface LeaderboardRow {
  readonly nameId: number;
  readonly team: number;
  readonly points: number;
}

/** `TEAM_BLUE` is 1 on the wire, not 0 (0 is reserved for unspecified); every team-keyed table on this side is 0-based, so storing the wire value directly draws players in the wrong team's colours. */
export function teamFromWire(team: Team): number {
  switch (team) {
    case Team.BLUE:
      return 0;
    case Team.RED:
      return 1;
    case Team.UNSPECIFIED:
    case Team.SPECTATOR:
      return 2;
  }
}
